import { FC, useState } from 'react'
import { KeyRound } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { CopyButton } from '@/components/global/copy-button'
import { RevealButton } from '@/components/global/reveal-button'
import SettingsCard from '@/components/settings-card'
import { Label } from '@/components/ui/label'

type Props = {
  widgetKey: string
  secretKey: string
}

const APIKeysCard: FC<Props> = ({ widgetKey, secretKey }) => {
  const [isVisible, setIsVisible] = useState(false)

  return (
    <SettingsCard
      Icon={KeyRound}
      title="API Credentials"
      description={
        <>
          Use these credentials with our API. View{' '}
          <a
            href="https://journeyid.com"
            className="underline hover:text-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            our documentation
          </a>{' '}
          for guides and an API reference.
        </>
      }
      sections={[
        <div key="widget-key">
          <Label>Widget Key</Label>
          <div className="flex items-center gap-2">
            <Input
              value={widgetKey}
              readOnly
              className="font-mono bg-muted-foreground/5"
              data-testid="widget-key-input"
            />
            <CopyButton text={widgetKey} />
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            This key can be used for API requests from the browser, typically from a widget on the Agent Desktop.
          </div>
        </div>,
        <div key="secret-key">
          <Label>Secret Key</Label>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={isVisible ? secretKey : secretKey.replace(/_.+/, match => '_' + '*'.repeat(match.length - 1))}
              readOnly
              className="font-mono bg-muted-foreground/5"
              data-testid="secret-key-input"
            />
            <RevealButton isVisible={isVisible} onToggle={() => setIsVisible(!isVisible)} />
            <CopyButton text={secretKey} />
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            This key is used for server-to-server API requests. This API key has full access to your account and must be
            kept secure.
          </div>
        </div>,
      ]}
      className="flex flex-col max-w-lg shadow-none min-w-md"
    />
  )
}

export default APIKeysCard
